import React, { useEffect, useRef } from 'react';
import useDataGrid from '@afosto/utils-react/datagrid/useDataGrid';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { useUsersContext } from '../UsersProvider';
import { DataGrid } from '@afosto/app-components-react/DataGrid';
import dataSourceRequest from '@afosto/utils-react/dataSource/dataSourceRequest';
import sortArrayByProperty from '@afosto/utils/array/sortArrayByProperty';
import { userFormatter, statusFormatter, inviteActionsFormatter } from '../formatters';
import { users as usersApi } from '@afosto/iam/api/Users';

const fetchInvites = async dataSource =>
  dataSourceRequest(
    dataSource,
    async (data, requestOptions) => usersApi.listUsers({ isPendingInvite: 1 }, requestOptions),
    {
      transformData: async data => {
        try {
          const promises = data.map(async user => {
            try {
              const tokenResponse = await usersApi.listTokens('invite', user.id);
              return { id: user.id, tokens: [...tokenResponse.data] };
            } catch (err) {
              return { id: user.id, tokens: [] };
            }
          });

          const tokenResponses = await Promise.all(promises);
          const tokens = tokenResponses.reduce((acc, token) => {
            const { id, ...restTokenData } = token;
            return { ...acc, [id]: restTokenData };
          }, {});

          const formattedUsers = data.reduce((acc, user) => {
            const hasValidTokens = tokens[user.id].tokens.some(token => {
              const tokenExpiresAt = new Date(token.expires_at).getTime();
              const currentTime = new Date().getTime();

              return tokenExpiresAt - currentTime > 0;
            });

            return [
              ...acc,
              {
                id: user.id,
                email: user.email,
                first_name: user.first_name,
                last_name: user.last_name,
                full_name: `${user.first_name} ${user.last_name}`,
                is_expired: !hasValidTokens,
              },
            ];
          }, []);

          return sortArrayByProperty(formattedUsers, 'full_name');
        } catch (err) {
          return [];
        }
      },
    },
  );

const UsersInvites = () => {
  const intl = useIntl();
  const { refreshDataGrid, setRefreshDataGrid } = useUsersContext();
  const dataSource = useDataGrid(fetchInvites, {
    modules: ['dataProperties', 'pagination'],
    dataProperties: {
      fixedProperties: [
        {
          key: 'user',
          name: intl.formatMessage({
            id: 'dataGrid.columns.invites.invitee',
            defaultMessage: 'Invitee',
            description: 'The column name for column inv',
          }),
          type: 'user',
        },
        {
          key: 'status',
          name: intl.formatMessage({
            id: 'dataGrid.columns.invites.status',
            defaultMessage: 'Status',
            description: 'The column name for column status',
          }),
          type: 'status',
          width: '150px',
        },
        {
          key: 'actions',
          name: intl.formatMessage({
            id: 'dataGrid.columns.invites.actions',
            defaultMessage: 'Actions',
            description: 'The column name for column actions',
          }),
          type: 'inviteActions',
          width: '140px',
          align: 'right',
        },
      ],
    },
  });
  const dataSourceRef = useRef(dataSource);

  useEffect(() => {
    dataSourceRef.current = dataSource;
  });

  useEffect(() => {
    if (refreshDataGrid) {
      dataSourceRef.current.reloadDataSource();
      setRefreshDataGrid(false);
    }
  }, [refreshDataGrid, setRefreshDataGrid]);

  return (
    <DataGrid
      dataSource={dataSource}
      formatters={{
        user: userFormatter,
        status: statusFormatter,
        inviteActions: inviteActionsFormatter,
      }}
    />
  );
};

export default UsersInvites;
